// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { ApiUser } from '../../types/accounts';
import { UserRole } from '../../types/auth';
import { authService } from '../../services/authService';
import { consoleService } from '../../services/consoleService';

// SVG Icons
const RedTriangleIcon = () => (
  <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="me-2" />
);

const GreenTickIcon = () => (
  <img src="/static/svg/green-tick.svg" alt="" width="16" height="16" className="me-2" />
);

interface WalletTabProps {
  account: ApiUser;
}

interface WalletData {
  coins: number;     // global coin
  ludo: number;      // ludo-specific coins
  quiz: number;      // quiz-specific coins
  solitaire: number; // solitaire-specific coins
}



const WalletTab: React.FC<WalletTabProps> = ({ account }) => {
  const [walletData, setWalletData] = useState<WalletData>({
    coins: 0,
    ludo: 0,
    quiz: 0,
    solitaire: 0
  });
  const [editingWallet, setEditingWallet] = useState<WalletData>({
    coins: 0,
    ludo: 0,
    quiz: 0,
    solitaire: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [updating, setUpdating] = useState(false);

  const sessionRole = authService.getSessionRole();

  useEffect(() => {
    loadWalletData();
  }, [account.id]);

  useEffect(() => {
    // Parse wallet data from account
    try {
      console.log('[WalletTab] Raw wallet string:', account.wallet);
      const wallet = account.wallet ? JSON.parse(account.wallet) : {};
      console.log('[WalletTab] Parsed wallet:', wallet);
      
      // Nakama wallet uses numeric keys based on currency type
      // Check backend currency.go for actual keys: 1=Coin, 2=LudoCoin, 3=QuizCoin, 4=SolitaireCoin
      const walletWithDefaults: WalletData = {
        coins: wallet['1'] || wallet.coins || wallet.Coin || 0,
        ludo: wallet['2'] || wallet.ludo || wallet.LudoCoin || 0,
        quiz: wallet['3'] || wallet.quiz || wallet.QuizCoin || 0,
        solitaire: wallet['4'] || wallet.solitaire || wallet.SolitaireCoin || 0
      };
      
      console.log('[WalletTab] Mapped wallet data:', walletWithDefaults);
      setWalletData(walletWithDefaults);
      setEditingWallet(walletWithDefaults);
    } catch (e) {
      console.error('Failed to parse wallet data:', e);
      const defaultWallet: WalletData = {
        coins: 0,
        ludo: 0,
        quiz: 0,
        solitaire: 0
      };
      setWalletData(defaultWallet);
      setEditingWallet(defaultWallet);
    }
  }, [account.wallet]);

  const loadWalletData = async () => {
    // Wallet data is already in the account object
    setLoading(false);
  };

  const updateAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const handleSaveWallet = async () => {
    setError('');
    setSuccess(false);
    setUpdating(true);

    try {
      // Send the full wallet values (not changeset)
      const walletValues: Record<string, number> = {
        coins: editingWallet.coins,
        ludo: editingWallet.ludo,
        quiz: editingWallet.quiz,
        solitaire: editingWallet.solitaire,
      };

      // Call the API to update wallet
      await consoleService.updateAccountWallet(account.id, walletValues);

      console.log('Update wallet values:', walletValues);
      
      setWalletData(editingWallet);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update wallet');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingWallet(walletData);
    setError('');
  };

  const handleWalletFieldChange = (field: keyof WalletData, value: string) => {
    const numValue = parseInt(value) || 0;
    setEditingWallet(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString();
  };

  if (loading) {
    return (
      <div className="wallet-tab">
        <div className="alert alert-info">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <strong>Loading wallet...</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-tab">
      {error && (
        <div className="alert alert-danger mb-3">
          <RedTriangleIcon />
          <h6 className="d-inline fw-bold mb-0">An error occurred: {error}</h6>
        </div>
      )}

      {success && (
        <div className="alert alert-success mb-3">
          <GreenTickIcon />
          <h6 className="d-inline fw-bold mb-0">Wallet was updated successfully.</h6>
        </div>
      )}

      {/* Current Wallet Balance */}
      <div className="wallet-balance-card">
        <div className="wallet-header">
          <div className="wallet-title">
            <span className="wallet-icon">💰</span>
            <h5 className="wallet-title-text">Wallet Balance</h5>
          </div>

        </div>
        
        <div className="wallet-content">
          <div className="coins-grid">
            {/* Global Coins */}
            <div className="coin-card global-coins">
              <div className="coin-header">
                <div className="coin-icon-wrapper">
                  <span className="coin-icon">🪙</span>
                </div>
                <div className="coin-label">
                  <h6>Global Coins</h6>
                  <span className="coin-subtitle">Universal Currency</span>
                </div>
              </div>
              <div className="coin-value">
                {updateAllowed() ? (
                  <div className="coin-input-wrapper">
                    <input
                      type="number"
                      className="coin-input"
                      value={editingWallet.coins}
                      onChange={(e) => handleWalletFieldChange('coins', e.target.value)}
                      min="0"
                      placeholder="0"
                    />
                    <span className="input-suffix">coins</span>
                  </div>
                ) : (
                  <div className="coin-amount global">
                    <span className="amount-number">{formatCurrency(walletData.coins)}</span>
                    <span className="amount-label">coins</span>
                  </div>
                )}
              </div>
            </div>

            {/* Ludo Coins */}
            <div className="coin-card ludo-coins">
              <div className="coin-header">
                <div className="coin-icon-wrapper">
                  <span className="coin-icon">🎲</span>
                </div>
                <div className="coin-label">
                  <h6>Ludo Coins</h6>
                  <span className="coin-subtitle">Board Game Currency</span>
                </div>
              </div>
              <div className="coin-value">
                {updateAllowed() ? (
                  <div className="coin-input-wrapper">
                    <input
                      type="number"
                      className="coin-input"
                      value={editingWallet.ludo}
                      onChange={(e) => handleWalletFieldChange('ludo', e.target.value)}
                      min="0"
                      placeholder="0"
                    />
                    <span className="input-suffix">coins</span>
                  </div>
                ) : (
                  <div className="coin-amount ludo">
                    <span className="amount-number">{formatCurrency(walletData.ludo)}</span>
                    <span className="amount-label">coins</span>
                  </div>
                )}
              </div>
            </div>

            {/* Quiz Coins */}
            <div className="coin-card quiz-coins">
              <div className="coin-header">
                <div className="coin-icon-wrapper">
                  <span className="coin-icon">🧠</span>
                </div>
                <div className="coin-label">
                  <h6>Quiz Coins</h6>
                  <span className="coin-subtitle">Knowledge Rewards</span>
                </div>
              </div>
              <div className="coin-value">
                {updateAllowed() ? (
                  <div className="coin-input-wrapper">
                    <input
                      type="number"
                      className="coin-input"
                      value={editingWallet.quiz}
                      onChange={(e) => handleWalletFieldChange('quiz', e.target.value)}
                      min="0"
                      placeholder="0"
                    />
                    <span className="input-suffix">coins</span>
                  </div>
                ) : (
                  <div className="coin-amount quiz">
                    <span className="amount-number">{formatCurrency(walletData.quiz)}</span>
                    <span className="amount-label">coins</span>
                  </div>
                )}
              </div>
            </div>

            {/* Solitaire Coins */}
            <div className="coin-card solitaire-coins">
              <div className="coin-header">
                <div className="coin-icon-wrapper">
                  <span className="coin-icon">🃏</span>
                </div>
                <div className="coin-label">
                  <h6>Solitaire Coins</h6>
                  <span className="coin-subtitle">Card Game Currency</span>
                </div>
              </div>
              <div className="coin-value">
                {updateAllowed() ? (
                  <div className="coin-input-wrapper">
                    <input
                      type="number"
                      className="coin-input"
                      value={editingWallet.solitaire}
                      onChange={(e) => handleWalletFieldChange('solitaire', e.target.value)}
                      min="0"
                      placeholder="0"
                    />
                    <span className="input-suffix">coins</span>
                  </div>
                ) : (
                  <div className="coin-amount solitaire">
                    <span className="amount-number">{formatCurrency(walletData.solitaire)}</span>
                    <span className="amount-label">coins</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Edit Actions */}
          {updateAllowed() && (
            <div className="wallet-actions">
              <button 
                className="wallet-btn wallet-btn-reset"
                onClick={handleCancelEdit}
                disabled={updating}
              >
                <span className="btn-icon">🔄</span>
                <span>Reset Changes</span>
              </button>
              <button 
                className="wallet-btn wallet-btn-save"
                onClick={handleSaveWallet}
                disabled={updating}
              >
                <span className="btn-icon">{updating ? '⏳' : '💾'}</span>
                <span>{updating ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default WalletTab;
